import { NgStyle, AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DOCUMENT,
    effect,
    ElementRef,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
    ViewChild,
    WritableSignal,
} from "@angular/core";

import { LoadingBarModule } from "@ngx-loading-bar/core";
import { combineLatest, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, startWith } from "rxjs/operators";

import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import {
    Context,
    StorageManager,
    StorageService,
    WINDOW,
} from "@hermes/core-app-core";
import { SeoLinksComponent } from "@hermes/hermes-web-fragments-seo-links";
import {
    BannersTopContainerComponent,
    AccountCreationBannerComponent,
} from "@hermes/domains-shell-fragments-banners";
import { MenuFacade } from "@hermes/domains-shell-states-menu-state";
import {
    FeatureFlagFacade,
    HEADER_FULL_TRANSPARENT,
} from "@hermes/hermes-web-states-flipper-state";
import { TrayStackService } from "@hermes/hermes-web-states-tray-state";
import {
    isEqual,
    ObserverFactory,
    WindowWithResizeObserver,
} from "@hermes/utils-generic-helpers";
import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

import {
    HEADER_DESKTOP_HEIGHT,
    HEADER_DESKTOP_HEIGHT_WITH_MENU,
    HEADER_MOBILE_HEIGHT,
} from "../../constants/header.constant";
import {
    calculateScrollLimit,
    getHeaderStateFromScroll,
} from "../../helpers/header.helper";
import { HeaderInnerState, PositionType } from "../../model/header.model";
import { HeaderBarComponent } from "../header-bar/header-bar.component";
import { HeaderLinksComponent } from "../header-links/header-links.component";
import { MenuBarComponent } from "../menu-bar/menu-bar.component";
import { activeMenu } from "../menu-bar/menu-bar.state";

@Component({
    selector: "h-header",
    imports: [
        NgStyle,
        LoadingBarModule,
        BannersTopContainerComponent,
        SeoLinksComponent,
        HeaderLinksComponent,
        HeaderBarComponent,
        MenuBarComponent,
        AccountCreationBannerComponent,
        AsyncPipe,
    ],
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy, AfterViewInit {
    private window = inject<WindowWithResizeObserver>(WINDOW);
    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);
    private context = inject(Context);
    private zone = inject(NgZone);
    private layoutFacade = inject(LayoutFacade);
    private trayStack = inject(TrayStackService);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private storageService = inject(StorageService);
    private breakpointService = inject(BreakpointService);
    public menuFacade = inject(MenuFacade);

    /**
     * Reference to the wrapper of main header content (menu, search, account, basket).
     */
    @ViewChild("headerMainContainer", { static: true })
    public headerMainContainerEl!: ElementRef<HTMLElement>;

    @ViewChild("bannerContainer", { static: true })
    public bannerContainerEl!: ElementRef<HTMLElement>;

    @ViewChild("headerMain", { static: true })
    public headerMainEl!: ElementRef<HTMLElement>;

    @ViewChild("headerAndBannerContainer", { static: true })
    public headerAndBannerContainerEl!: ElementRef<HTMLElement>;

    @ViewChild("headerBackgroundFixed", { static: true })
    public headerBackgroundFixedEl!: ElementRef<HTMLElement>;

    @ViewChild("headerLinksContainer", { static: true })
    public headerLinksContainerEl!: ElementRef<HTMLElement>;

    @ViewChild("menuBar", { static: true })
    public menuBarEL!: ElementRef<HTMLElement>;

    public readonly HEADER_MAIN_DESKTOP_HEIGHT = HEADER_DESKTOP_HEIGHT;
    public readonly HEADER_MAIN_MOBILE_HEIGHT = HEADER_MOBILE_HEIGHT;
    public readonly HEADER_DESKTOP_HEIGHT_WITH_MENU =
        HEADER_DESKTOP_HEIGHT_WITH_MENU;

    /**
     * State of the header
     */
    public headerState: WritableSignal<HeaderInnerState> = signal({
        headerPosition: PositionType.Relative,
        isOnlyMenuBarDisplayed: false,
    });

    public shouldBeTransparent = computed(() => {
        const headerState = this.headerState();
        return (
            !this.isSubMenuActive() &&
            headerState.headerPosition !== PositionType.Fixed
        );
    });

    public isHeaderTransparent$: Observable<boolean>;

    public isInServerMode: boolean;

    public isUnderMenuBreakpoint$: Observable<boolean>;

    /**
     * Inputs for account creation banner
     */
    public isAccountCreated: boolean = false;
    public isAccountCreatedByWechat: boolean = false;
    public isSubMenuActive = signal(false);
    public subscription: Subscription = new Subscription();
    private sessionStorage: StorageManager | undefined;
    private lastScrollY: number;
    public isHeaderFullTransparentFF$: Observable<boolean> =
        this.featureFlagFacade.isActivated(HEADER_FULL_TRANSPARENT);
    /* Provide resizeObserver */
    private observerFactory: ObserverFactory;

    constructor() {
        this.sessionStorage = this.storageService.getSessionStorageInstance();
        this.lastScrollY = this.window.scrollY;
        this.isHeaderTransparent$ = this.layoutFacade.headerTransparent$;
        this.isInServerMode = this.context.isInServerMode();

        this.observerFactory = new ObserverFactory();

        this.isUnderMenuBreakpoint$ = this.breakpointService
            .mediumBreakpointObserver()
            .pipe(map((value) => !value.matches));
        effect(() => {
            this.menuFacade.fetchMenu();
        });
        effect(() => {
            const currentActiveMenu = activeMenu();

            const isSubMenuOpen =
                !!currentActiveMenu && currentActiveMenu.isActive();

            // Update submenu
            this.isSubMenuActive?.set(isSubMenuOpen);

            // update header transparency state
            this.layoutFacade.setHeaderTransparency(this.shouldBeTransparent());

            this.layoutFacade.setBlackOverlayVisible(isSubMenuOpen);
        });
    }
    public ngOnInit(): void {
        if (this.context.isInBrowserMode()) {
            this.getAccountCreationBanner();
        }

        let scrollBarWidth = 0;

        setTimeout(() => {
            scrollBarWidth =
                this.window.innerWidth -
                this.document.documentElement.clientWidth;
        });

        this.subscription.add(
            this.trayStack.trays$
                .pipe(
                    filter(
                        () =>
                            this.headerState().headerPosition ===
                                PositionType.Fixed &&
                            this.breakpointService.mediumBreakpointMatcher(),
                    ),
                    map((trays) => trays.some((tray) => tray.isOpen)),
                    distinctUntilChanged(),
                )
                .subscribe((isOpen) => {
                    // When a tray is opened, additional right padding is added to make up for the scrollbar disappearing,
                    // to avoid header content from shifting.
                    isOpen
                        ? this.renderer.setStyle(
                              this.headerMainContainerEl.nativeElement,
                              "right",
                              `${scrollBarWidth}px`,
                          )
                        : this.renderer.removeStyle(
                              this.headerMainContainerEl.nativeElement,
                              "right",
                          );
                }),
        );
    }

    public ngAfterViewInit(): void {
        const fullHeight =
            this.headerMainContainerEl.nativeElement.offsetHeight;
        this.layoutFacade.setOriginalFullHeaderHeight(fullHeight);
        if (this.context.isInBrowserMode()) {
            this.zone.runOutsideAngular(() => {
                this.window.addEventListener("scroll", this.scrollHandler);
            });

            this.subscription.add(
                combineLatest([
                    this.observerFactory
                        .observeResize(
                            this.headerAndBannerContainerEl.nativeElement,
                        )
                        .pipe(startWith(0), distinctUntilChanged()),
                    this.observerFactory
                        .observeResize(this.headerMainContainerEl.nativeElement)
                        .pipe(startWith(0), distinctUntilChanged()),
                ]).subscribe(() => {
                    this.updateStoreHeaderHeight();
                }),
            );
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.context.isInBrowserMode()) {
            this.window.removeEventListener("scroll", this.scrollHandler);
        }
    }

    public closeAccountCreationBanner(): void {
        this.isAccountCreated = false;
        this.isAccountCreatedByWechat = false;
    }

    /* scroll handler, in an arrow function to be able to cancel it.
     * And to treat it outside angular.
     */
    private scrollHandler = (): void => {
        const currentScrollY = this.window.scrollY;
        const isScrollingDown = currentScrollY > this.lastScrollY;
        const isMenuHeaderActivated =
            this.breakpointService.mediumBreakpointMatcher();

        const scrollLimit = calculateScrollLimit(
            this.bannerContainerEl.nativeElement.offsetHeight,
            this.headerMainEl.nativeElement.offsetHeight,
            isScrollingDown,
            isMenuHeaderActivated,
        );

        const newHeaderState = getHeaderStateFromScroll(
            scrollLimit,
            currentScrollY,
            isScrollingDown,
            isMenuHeaderActivated,
        );

        const isChangingPositionType =
            this.headerState().headerPosition !== newHeaderState.headerPosition;

        if (!isEqual(this.headerState(), newHeaderState)) {
            this.zone.run(() => {
                if (isChangingPositionType) {
                    // We don't want animation to play when going from relative to fixed and vice-versa
                    this.headerMainContainerEl.nativeElement.classList.add(
                        "no-transition",
                    );

                    this.headerState.set(newHeaderState);
                    this.updateStoreHeaderHeight();

                    // Re-enable transitions after a small delay
                    setTimeout(() => {
                        this.headerMainContainerEl.nativeElement.classList.remove(
                            "no-transition",
                        );
                    }, 10);
                } else {
                    this.headerState.set(newHeaderState);
                    this.updateStoreHeaderHeight();
                }
            });
        }
        this.lastScrollY = currentScrollY;
    };

    private getAccountCreationBanner() {
        this.isAccountCreated = this.checkAccountCreationBannerVisibility();
        if (this.isAccountCreated) {
            this.isAccountCreatedByWechat = !!this.sessionStorage?.getItem(
                "accountCreatedByWechat",
            );
            this.sessionStorage?.deleteItem("accountCreated");
            this.sessionStorage?.deleteItem("accountCreatedByWechat");
        }
    }

    private checkAccountCreationBannerVisibility(): boolean {
        return !!this.sessionStorage?.getItem("accountCreated");
    }

    private updateStoreHeaderHeight(): void {
        this.setGlobalHeaderTopProperty();
        this.layoutFacade.updateHeaderProperties({
            headerHeightWithLinks:
                this.headerMainContainerEl.nativeElement.offsetHeight +
                this.headerLinksContainerEl.nativeElement.offsetHeight,
            headerMainHeight: this.headerState().isOnlyMenuBarDisplayed
                ? this.menuBarEL.nativeElement.offsetHeight
                : this.headerMainContainerEl.nativeElement.offsetHeight,
            headerAndBannerHeight:
                this.headerAndBannerContainerEl.nativeElement.offsetHeight,
            menubarHeight: this.menuBarEL.nativeElement.offsetHeight,
            isFixed: this.headerState().headerPosition === PositionType.Fixed,
        });
    }

    /**
     * We store the top header position in global CSS variable.
     * All elements that need to position under menu car use var(--header-menu-top).
     */
    private setGlobalHeaderTopProperty() {
        let headerTopHeight = 0;
        if (this.headerState().headerPosition === PositionType.Fixed) {
            headerTopHeight =
                this.headerState().isOnlyMenuBarDisplayed === true
                    ? this.menuBarEL.nativeElement.offsetHeight
                    : this.headerMainContainerEl.nativeElement.offsetHeight;
        }
        this.renderer.setStyle(
            document.documentElement,
            "--header-menu-top",
            `${headerTopHeight}px`,
            2,
        );
    }
}
